import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from '../axios-config/axiosInstance.ts';
import { IClientReq, IClientResponse, ICommonAPIResponse, ICommonListPayloadDto, IClientListResponse, IClientAPIResponse } from '../type/type';


interface ClientState {
  clients: IClientResponse[];
  loading: boolean;
  error: string | null;
  clientSelect: {
    label: string;
    value: number;
  }[];
  getClientbyIdvalue: any;
  offset: number;
  limit: number;
  sortField: string;
  sortOrder: number;
  searchInput: string;
  dataCount: number;
  getIUserByIdValue: any

}

const initialState: ClientState = {
  clients: [],
  clientSelect: [],
  loading: false,
  error: null,
  getClientbyIdvalue: [],
  offset: 0,
  limit: 10,
  sortField: 'createdOn',
  sortOrder: 1,
  searchInput: '',
  dataCount: 0,
  getIUserByIdValue: []
};



export const createClient = createAsyncThunk<
  IClientResponse,
  IClientReq & { rejectValue: string },
  { rejectValue: string }
>(
  'client/create',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await Axios.post<IClientResponse>('customers', {
        firstname: clientData.firstname,
        lastname: clientData.lastname,
        email: clientData.email,
        mobile: clientData.mobile,
        age: clientData.age,
        status: clientData.status,
        TFN_number: clientData.TFN_number,
        license: clientData.license,
        proofage: clientData.proofage,
        passport: clientData.passport,
        city: clientData.city,
        state: clientData.state,
        address: clientData.address,
        zipcode: clientData.zipcode,
        latitude: clientData.latitude,
        longitude: clientData.longitude
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create client');
    }
  }
);

export const getAllClients = createAsyncThunk<
  IClientListResponse,
  ICommonListPayloadDto,
  { rejectValue: string }
>(
  'customer',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.get<ICommonAPIResponse>('customers', { params: payload });
      if (!response.data.success) {
        return rejectWithValue(response.data.message);
      }
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
    }
  }
);

export const fetchClient = createAsyncThunk<
  IClientAPIResponse,
  { clientId: number },
  { rejectValue: string }
>(
  'client/fetchById',
  async ({ clientId }, { rejectWithValue }) => {
    try {
      const response = await Axios.get<IClientAPIResponse>(`customers/${clientId}`); 
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client');
    }
  }
);  


export const updateClient = createAsyncThunk<
  IClientResponse,
  IClientReq & { id: number },
  { rejectValue: string }
>(
  'client/updateClient',
  async (value, { rejectWithValue }) => {
    try {
      const response = await Axios.put<IClientResponse>(`customers/${value.id}`, {
        firstname: value.firstname,
        lastname: value.lastname,
        email: value.email,
        mobile: value.mobile,
        age: value.age,
        status: value.status,
        TFN_number: value.TFN_number,
        license: value.license,
        proofage: value.proofage,
        passport: value.passport,
        city: value.city,
        state: value.state,
        address: value.address,
        zipcode: value.zipcode,
        latitude: value.latitude,
        longitude: value.longitude
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update client');
    }
  }
);


export const deleteClient = createAsyncThunk<number, number, { rejectValue: string }>(
  "client/deleteClient",
  async (id, { rejectWithValue }) => {
    try {
      await Axios.delete(`customers/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete client");
    }
  }
);


const clientSlice = createSlice({
  name: 'clientState',
  initialState,
  reducers: {
    setOffset: (state, action) => {
      state.offset = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSortField: (state, action) => {
      state.sortField = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload.data;
        state.dataCount = action.payload.total;
      })
      .addCase(getAllClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClient.fulfilled, (state, action) => {
        state.loading = false;
        state.getClientbyIdvalue = action.payload;
      })
      .addCase(fetchClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteClient.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});


export const { setOffset, setLimit, setSortField, setSortOrder, setSearchInput } = clientSlice.actions;
export default clientSlice.reducer;